/**
 * React Query hooks for Work Arrangement operations.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import workArrangementService from '../services/work-arrangement.service';

interface UseWorkArrangementListQueryParams {
  workspaceId?: string;
  idToken: string | undefined;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useWorkArrangementListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 50,
  sortBy = 'name',
  order = 'asc',
  search,
  enabled = true,
  refetchInterval = 5000,
}: UseWorkArrangementListQueryParams) => {
  return useQuery({
    queryKey: ['work-arrangements', workspaceId, page, size, sortBy, order, search],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }

      return await workArrangementService.listWorkArrangements(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        search,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval,
    refetchIntervalInBackground: true,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseWorkArrangementDetailQueryParams {
  workArrangementId: string | undefined;
  workspaceId?: string;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useWorkArrangementDetailQuery = ({
  workArrangementId,
  workspaceId,
  idToken,
  enabled = true,
}: UseWorkArrangementDetailQueryParams) => {
  return useQuery({
    queryKey: ['work-arrangement', workspaceId, workArrangementId],
    queryFn: async () => {
      if (!workArrangementId || !workspaceId || !idToken) {
        throw new Error('Work arrangement ID, workspace ID, and token are required');
      }

      return await workArrangementService.getWorkArrangement(workspaceId, workArrangementId, idToken);
    },
    enabled: enabled && !!workspaceId && !!workArrangementId && !!idToken,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};
